import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { ArrowLeft, User as UserIcon, Mail, Lock, Save, Upload, Trash2, Camera } from "lucide-react";

export default function Profile() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get("/auth/me");
      setUser(response.data);
      setName(response.data.name || "");
      setEmail(response.data.email || "");
      
      // Set avatar preview if exists
      if (response.data.avatarUrl) {
        setAvatarPreview(`${process.env.REACT_APP_BACKEND_URL}${response.data.avatarUrl}`);
      }
    } catch (error) {
      toast.error("Fehler beim Laden des Profils");
    }
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file type
      if (!file.type.startsWith('image/')) {
        toast.error("Bitte nur Bilddateien hochladen");
        return;
      }

      setAvatarFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadAvatar = async () => {
    if (!avatarFile) {
      toast.error("Bitte wählen Sie ein Bild aus");
      return;
    }

    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('file', avatarFile);

      const response = await axios.post("/users/avatar", formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success("Avatar erfolgreich hochgeladen!");
      setAvatarFile(null);
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Hochladen");
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleDeleteAvatar = async () => {
    if (!user?.avatarUrl) return;

    try {
      await axios.delete("/users/avatar");
      toast.success("Avatar gelöscht");
      setAvatarPreview(null);
      setAvatarFile(null);
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    
    if (newPassword && newPassword !== confirmPassword) {
      toast.error("Passwörter stimmen nicht überein");
      return;
    }

    if (newPassword && !currentPassword) {
      toast.error("Bitte aktuelles Passwort eingeben");
      return;
    }

    setLoading(true);
    try {
      const updates = {
        name: name || undefined,
        email: email || undefined,
      };

      if (newPassword && currentPassword) {
        updates.currentPassword = currentPassword;
        updates.newPassword = newPassword;
      }

      await axios.put("/auth/profile", updates);
      toast.success("Profil erfolgreich aktualisiert!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Aktualisieren");
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-button"
              variant="outline"
              size="icon"
              onClick={() => navigate("/")}
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-2xl font-bold text-gray-900">Profil bearbeiten</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Avatar Upload */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Camera className="w-5 h-5" />
                Profilbild
              </CardTitle>
              <CardDescription>
                Lade ein Profilbild hoch
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row items-center gap-6">
                {/* Avatar Display */}
                <div className="relative">
                  <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                    {avatarPreview ? (
                      <img 
                        src={avatarPreview} 
                        alt="Avatar" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <UserIcon className="w-16 h-16 text-gray-400" />
                    )}
                  </div>
                </div>

                {/* Upload Controls */}
                <div className="flex-1 space-y-4">
                  <div>
                    <Label htmlFor="avatar-upload" className="cursor-pointer">
                      <div className="flex items-center gap-2 text-blue-600 hover:text-blue-700">
                        <Upload className="w-4 h-4" />
                        Bild auswählen
                      </div>
                    </Label>
                    <Input
                      id="avatar-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarChange}
                      className="hidden"
                    />
                    {avatarFile && (
                      <p className="text-sm text-gray-600 mt-2">
                        Ausgewählt: {avatarFile.name}
                      </p>
                    )}
                  </div>

                  <div className="flex gap-2">
                    {avatarFile && (
                      <Button
                        type="button"
                        onClick={handleUploadAvatar}
                        disabled={uploadingAvatar}
                        size="sm"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        {uploadingAvatar ? "Hochladen..." : "Hochladen"}
                      </Button>
                    )}
                    {user?.avatarUrl && (
                      <Button
                        type="button"
                        variant="destructive"
                        onClick={handleDeleteAvatar}
                        size="sm"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Löschen
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <form onSubmit={handleUpdateProfile} className="space-y-6">
            {/* Personal Information */}
            <Card data-testid="personal-info-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserIcon className="w-5 h-5" />
                Persönliche Informationen
              </CardTitle>
              <CardDescription>
                Aktualisiere deine Profildaten
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">Benutzername</Label>
                <Input
                  id="username"
                  value={user.username}
                  disabled
                  className="bg-gray-100"
                />
                <p className="text-sm text-gray-500">Benutzername kann nicht geändert werden</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  data-testid="name-input"
                  placeholder="Ihr vollständiger Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  E-Mail
                </Label>
                <Input
                  id="email"
                  data-testid="email-input"
                  type="email"
                  placeholder="ihre@email.de"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Password Change */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5" />
                Passwort ändern
              </CardTitle>
              <CardDescription>
                Lasse die Felder leer, wenn Du das Passwort nicht ändern möchtest
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current-password">Aktuelles Passwort</Label>
                <Input
                  id="current-password"
                  data-testid="current-password-input"
                  type="password"
                  placeholder="Aktuelles Passwort"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-password">Neues Passwort</Label>
                <Input
                  id="new-password"
                  data-testid="new-password-input"
                  type="password"
                  placeholder="Neues Passwort"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">Passwort bestätigen</Label>
                <Input
                  id="confirm-password"
                  data-testid="confirm-password-input"
                  type="password"
                  placeholder="Neues Passwort bestätigen"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/")}
            >
              Abbrechen
            </Button>
            <Button
              type="submit"
              data-testid="save-profile-button"
              disabled={loading}
              className="gap-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
            >
              <Save className="w-4 h-4" />
              {loading ? "Speichern..." : "Änderungen speichern"}
            </Button>
          </div>
          </form>
        </div>
      </main>
    </div>
  );
}
